import React, { useRef, useEffect } from 'react'
import { Input, Button, Avatar, Spin, Empty, Alert } from 'antd'
import { UserOutlined, RobotOutlined, SendOutlined } from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import { useChatbotStore } from './Chatbot.store'
import CustomerContextPanel from './CustomerContextPanel'
import './Chatbot.scss'

const Chatbot: React.FC = () => {
  const { t, ready } = useI18n('modules')
  const {
    messages,
    currentInput,
    isLoading,
    connectionStatus,
    sendMessage,
    setCurrentInput,
    connect,
    disconnect,
    clearMessages,
  } = useChatbotStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const connectRef = useRef(connect)
  const tRef = useRef(t)
  connectRef.current = connect
  tRef.current = t

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // 组件加载时自动连接WebSocket（只在 ready 首次为 true 时触发一次）
  useEffect(() => {
    if (ready) {
      connectRef.current(tRef.current('chatbot.welcomeMessage'))
    }
    return () => {
      useChatbotStore.getState().disconnect()
    }
  }, [ready])

  const handleSend = async () => {
    if (!currentInput.trim()) return
    // 硬编码指令：/clear 清除对话
    if (currentInput.trim() === '/clear') {
      clearMessages()
      localStorage.removeItem('chatbot_conversation_id')
      setCurrentInput('')
      disconnect()
      setTimeout(() => connect(), 100)
      return
    }
    await sendMessage(currentInput)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="chatbot">
      <div className="chatbot-header">
        <h1>{t('chatbot.title')}</h1>
        <Alert
          message={t('chatbot.alerts.ready')}
          type="info"
          showIcon
        />
      </div>

      <div className="chatbot-body">
        <div className="chatbot-container">
          <div className="chatbot-messages">
            {messages.length === 0 ? (
              <div className="empty-state-container">
                <Empty description={t('chatbot.empty.noMessages')} />
              </div>
            ) : (
              <div className="messages-list">
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`message ${message.sender} ${message.isIntermediate ? 'intermediate' : ''} ${message.displayMode === 'streaming' ? 'streaming' : ''}`}
                  >
                    <div className="message-avatar">
                      {message.sender === 'user' ? (
                        <Avatar icon={<UserOutlined />} />
                      ) : (
                        <Avatar icon={<RobotOutlined />} />
                      )}
                    </div>
                    <div className="message-content">
                      <div className="message-text">
                        {message.content}
                        <span className="message-time">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="message bot">
                    <div className="message-avatar">
                      <Avatar icon={<RobotOutlined />} />
                    </div>
                    <div className="message-content">
                      <div className="message-text">
                        <Spin size="small" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="chatbot-input">
            <Input.TextArea
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chatbot.input.placeholder')}
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="message-input"
              id="chat-message"
              name="message"
              aria-label={t('chatbot.input.ariaLabel')}
              autoFocus
            />
            <Button
              type="primary"
              onClick={handleSend}
              loading={isLoading}
              disabled={!currentInput.trim()}
              className="send-button"
              aria-label={t('chatbot.input.sendAriaLabel')}
              icon={!isLoading ? <SendOutlined /> : undefined}
            />
          </div>
        </div>

        <CustomerContextPanel platform="web" platformUserId="test_user" />
      </div>
    </div>
  )
}

export default Chatbot